import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";

const StableIdSchema = Type.String({
  minLength: 1,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$",
});
const IdempotencyKeySchema = Type.String({
  minLength: 8,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{7,127}$",
});
const CanonicalDateTimeSchema = Type.String({ minLength: 24, maxLength: 24 });
const NullableDateTimeSchema = Type.Union([CanonicalDateTimeSchema, Type.Null()]);
const NullableTextSchema = Type.Union([
  Type.String({ minLength: 2, maxLength: 200, pattern: "^\\S(?:[\\s\\S]*\\S)?$" }),
  Type.Null(),
]);
const FNV_1A_64_OFFSET = 0xcbf29ce484222325n;
const FNV_1A_64_PRIME = 0x100000001b3n;

export const MEMBER_MARKETING_CONTRACT_VERSION = "1.0.0" as const;

export const MemberMarketingChannelSchema = Type.Union([
  Type.Literal("SMS"),
  Type.Literal("WECHAT"),
]);
export type MemberMarketingChannel = Static<typeof MemberMarketingChannelSchema>;

export const MemberMarketingConsentStateSchema = Type.Union([
  Type.Literal("NOT_GRANTED"),
  Type.Literal("GRANTED"),
  Type.Literal("OPTED_OUT"),
]);
export type MemberMarketingConsentState = Static<typeof MemberMarketingConsentStateSchema>;

export const MemberMarketingFrequencySchema = Type.Union([
  Type.Literal("WEEKLY"),
  Type.Literal("MONTHLY"),
]);
export type MemberMarketingFrequency = Static<typeof MemberMarketingFrequencySchema>;

export const MemberMarketingConsentSourceSchema = Type.Union([
  Type.Literal("IN_STORE_WRITTEN"),
  Type.Literal("STAFF_RECORDED_REQUEST"),
  Type.Literal("PUBLIC_UNSUBSCRIBE_LINK"),
  Type.Null(),
]);
export type MemberMarketingConsentSource = Static<typeof MemberMarketingConsentSourceSchema>;

export const MemberMarketingConsentReadModelSchema = Type.Object(
  {
    channel: MemberMarketingChannelSchema,
    available: Type.Boolean(),
    unavailableReason: NullableTextSchema,
    contactMasked: Type.Union([Type.String({ minLength: 4, maxLength: 80 }), Type.Null()]),
    state: MemberMarketingConsentStateSchema,
    frequency: Type.Union([MemberMarketingFrequencySchema, Type.Null()]),
    source: MemberMarketingConsentSourceSchema,
    sourceDetail: NullableTextSchema,
    version: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    updatedAt: NullableDateTimeSchema,
  },
  { additionalProperties: false },
);
export type MemberMarketingConsentReadModel = Static<typeof MemberMarketingConsentReadModelSchema>;

export const MemberMarketingMessageStateSchema = Type.Union([
  Type.Literal("QUEUED_FOR_PROVIDER"),
  Type.Literal("CANCELLED_AFTER_OPT_OUT"),
]);
export type MemberMarketingMessageState = Static<typeof MemberMarketingMessageStateSchema>;

export const MemberMarketingMessageReadModelSchema = Type.Object(
  {
    messageId: StableIdSchema,
    messageNumber: Type.String({ pattern: "^YX-[0-9]{8}-[0-9A-F]{12}$" }),
    memberId: StableIdSchema,
    channel: MemberMarketingChannelSchema,
    subject: Type.String({ minLength: 2, maxLength: 60 }),
    body: Type.String({ minLength: 2, maxLength: 500 }),
    state: MemberMarketingMessageStateSchema,
    consentVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    unsubscribeToken: Type.String({
      minLength: 43,
      maxLength: 43,
      pattern: "^[A-Za-z0-9_-]{43}$",
    }),
    queuedAt: CanonicalDateTimeSchema,
    cancelledAt: NullableDateTimeSchema,
  },
  { additionalProperties: false },
);
export type MemberMarketingMessageReadModel = Static<typeof MemberMarketingMessageReadModelSchema>;

export const MemberMarketingWorkspaceSchema = Type.Object(
  {
    contractVersion: Type.Literal(MEMBER_MARKETING_CONTRACT_VERSION),
    tenantId: StableIdSchema,
    storeId: StableIdSchema,
    memberId: StableIdSchema,
    memberNumber: Type.String({ minLength: 1, maxLength: 32 }),
    memberName: Type.String({ minLength: 1, maxLength: 80 }),
    memberStatus: Type.Union([
      Type.Literal("ACTIVE"),
      Type.Literal("FROZEN"),
      Type.Literal("DISABLED"),
    ]),
    consents: Type.Array(MemberMarketingConsentReadModelSchema, {
      minItems: 2,
      maxItems: 2,
    }),
    messages: Type.Array(MemberMarketingMessageReadModelSchema, { maxItems: 100 }),
    generatedAt: CanonicalDateTimeSchema,
  },
  { additionalProperties: false },
);
export type MemberMarketingWorkspace = Static<typeof MemberMarketingWorkspaceSchema>;

export const DecideMemberMarketingConsentRequestSchema = Type.Object(
  {
    contractVersion: Type.Literal(MEMBER_MARKETING_CONTRACT_VERSION),
    channel: MemberMarketingChannelSchema,
    decision: Type.Union([Type.Literal("GRANT"), Type.Literal("OPT_OUT")]),
    frequency: Type.Union([MemberMarketingFrequencySchema, Type.Null()]),
    evidenceSource: Type.Union([
      Type.Literal("IN_STORE_WRITTEN"),
      Type.Literal("STAFF_RECORDED_REQUEST"),
    ]),
    evidenceNote: Type.String({ minLength: 2, maxLength: 200 }),
    expectedConsentVersion: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type DecideMemberMarketingConsentRequest = Static<
  typeof DecideMemberMarketingConsentRequestSchema
>;

export const QueueMemberMarketingMessageRequestSchema = Type.Object(
  {
    contractVersion: Type.Literal(MEMBER_MARKETING_CONTRACT_VERSION),
    channel: MemberMarketingChannelSchema,
    subject: Type.String({ minLength: 2, maxLength: 60 }),
    body: Type.String({ minLength: 2, maxLength: 500 }),
    expectedConsentVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type QueueMemberMarketingMessageRequest = Static<
  typeof QueueMemberMarketingMessageRequestSchema
>;

export const MemberMarketingOperationResultSchema = Type.Object(
  {
    operation: Type.Union([Type.Literal("DECIDE_CONSENT"), Type.Literal("QUEUE_MESSAGE")]),
    workspace: MemberMarketingWorkspaceSchema,
    message: Type.Union([MemberMarketingMessageReadModelSchema, Type.Null()]),
  },
  { additionalProperties: false },
);
export type MemberMarketingOperationResult = Static<typeof MemberMarketingOperationResultSchema>;

export const PublicMemberMarketingUnsubscribeRequestSchema = Type.Object(
  {
    contractVersion: Type.Literal(MEMBER_MARKETING_CONTRACT_VERSION),
    token: Type.String({ minLength: 43, maxLength: 43, pattern: "^[A-Za-z0-9_-]{43}$" }),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type PublicMemberMarketingUnsubscribeRequest = Static<
  typeof PublicMemberMarketingUnsubscribeRequestSchema
>;

export const PublicMemberMarketingUnsubscribeResultSchema = Type.Object(
  {
    contractVersion: Type.Literal(MEMBER_MARKETING_CONTRACT_VERSION),
    channel: MemberMarketingChannelSchema,
    state: Type.Literal("OPTED_OUT"),
    cancelledQueuedMessageCount: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    effectiveAt: CanonicalDateTimeSchema,
  },
  { additionalProperties: false },
);
export type PublicMemberMarketingUnsubscribeResult = Static<
  typeof PublicMemberMarketingUnsubscribeResultSchema
>;

function canonicalEpoch(value: string): number | null {
  const epoch = Date.parse(value);
  return Number.isFinite(epoch) && new Date(epoch).toISOString() === value ? epoch : null;
}

function isRealDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(value)) return false;
  const epoch = Date.parse(`${value}T00:00:00.000Z`);
  return Number.isFinite(epoch) && new Date(epoch).toISOString() === `${value}T00:00:00.000Z`;
}

export function deriveMemberMarketingMessageNumber(
  businessDate: string,
  messageId: string,
): string {
  if (!isRealDate(businessDate) || !Value.Check(StableIdSchema, messageId)) {
    throw new TypeError("member marketing message identity is invalid");
  }
  let hash = FNV_1A_64_OFFSET;
  for (const character of `${businessDate}\0${messageId}`) {
    hash ^= BigInt(character.codePointAt(0) ?? 0);
    hash = BigInt.asUintN(64, hash * FNV_1A_64_PRIME);
  }
  const suffix = BigInt.asUintN(48, hash).toString(16).toUpperCase().padStart(12, "0");
  return `YX-${businessDate.replaceAll("-", "")}-${suffix}`;
}

export function isDecideMemberMarketingConsentRequest(
  value: unknown,
): value is DecideMemberMarketingConsentRequest {
  if (!Value.Check(DecideMemberMarketingConsentRequestSchema, value)) return false;
  return (
    value.evidenceNote === value.evidenceNote.trim() &&
    (value.decision === "GRANT"
      ? value.frequency !== null && value.evidenceSource === "IN_STORE_WRITTEN"
      : value.frequency === null && value.evidenceSource === "STAFF_RECORDED_REQUEST")
  );
}

export function isQueueMemberMarketingMessageRequest(
  value: unknown,
): value is QueueMemberMarketingMessageRequest {
  return (
    Value.Check(QueueMemberMarketingMessageRequestSchema, value) &&
    value.subject === value.subject.trim() &&
    value.body === value.body.trim()
  );
}

export function isPublicMemberMarketingUnsubscribeRequest(
  value: unknown,
): value is PublicMemberMarketingUnsubscribeRequest {
  return Value.Check(PublicMemberMarketingUnsubscribeRequestSchema, value);
}

export function isMemberMarketingConsentReadModel(
  value: unknown,
): value is MemberMarketingConsentReadModel {
  if (!Value.Check(MemberMarketingConsentReadModelSchema, value)) return false;
  if (!value.available) {
    return (
      value.unavailableReason !== null &&
      value.contactMasked === null &&
      value.state === "NOT_GRANTED" &&
      value.frequency === null &&
      value.source === null &&
      value.sourceDetail === null &&
      value.version === 0 &&
      value.updatedAt === null
    );
  }
  if (value.unavailableReason !== null || value.contactMasked === null) return false;
  if (value.state === "NOT_GRANTED") {
    return (
      value.frequency === null &&
      value.source === null &&
      value.sourceDetail === null &&
      value.version === 0 &&
      value.updatedAt === null
    );
  }
  return (
    value.version >= 1 &&
    value.updatedAt !== null &&
    canonicalEpoch(value.updatedAt) !== null &&
    value.source !== null &&
    value.sourceDetail !== null &&
    (value.state === "GRANTED") === (value.frequency !== null)
  );
}

export function isMemberMarketingMessageReadModel(
  value: unknown,
): value is MemberMarketingMessageReadModel {
  if (!Value.Check(MemberMarketingMessageReadModelSchema, value)) return false;
  const queuedAt = canonicalEpoch(value.queuedAt);
  const cancelledAt = value.cancelledAt === null ? null : canonicalEpoch(value.cancelledAt);
  if (queuedAt === null) return false;
  let expectedNumber: string;
  try {
    expectedNumber = deriveMemberMarketingMessageNumber(
      value.queuedAt.slice(0, 10),
      value.messageId,
    );
  } catch {
    return false;
  }
  return (
    value.messageNumber === expectedNumber &&
    (value.state === "QUEUED_FOR_PROVIDER"
      ? value.cancelledAt === null
      : cancelledAt !== null && cancelledAt >= queuedAt)
  );
}

export function isMemberMarketingWorkspace(value: unknown): value is MemberMarketingWorkspace {
  if (!Value.Check(MemberMarketingWorkspaceSchema, value)) return false;
  if (canonicalEpoch(value.generatedAt) === null) return false;
  const channels = value.consents.map((consent) => consent.channel);
  if (
    channels[0] !== "SMS" ||
    channels[1] !== "WECHAT" ||
    !value.consents.every(isMemberMarketingConsentReadModel)
  ) {
    return false;
  }
  const ids = new Set<string>();
  return value.messages.every((message) => {
    if (
      !isMemberMarketingMessageReadModel(message) ||
      message.memberId !== value.memberId ||
      ids.has(message.messageId)
    ) {
      return false;
    }
    ids.add(message.messageId);
    return true;
  });
}

export function isMemberMarketingOperationResult(
  value: unknown,
): value is MemberMarketingOperationResult {
  return (
    Value.Check(MemberMarketingOperationResultSchema, value) &&
    isMemberMarketingWorkspace(value.workspace) &&
    (value.operation === "QUEUE_MESSAGE") === (value.message !== null) &&
    (value.message === null ||
      (isMemberMarketingMessageReadModel(value.message) &&
        value.message.memberId === value.workspace.memberId))
  );
}

export function isPublicMemberMarketingUnsubscribeResult(
  value: unknown,
): value is PublicMemberMarketingUnsubscribeResult {
  return (
    Value.Check(PublicMemberMarketingUnsubscribeResultSchema, value) &&
    canonicalEpoch(value.effectiveAt) !== null
  );
}
